import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Crosshair } from "@phosphor-icons/react";

const HERO_IMG = "/assets/hero-bg.jpg";

const MaskedLine = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Rifle detail — you are the new founding fathers" className="h-[125%] w-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
      </motion.div>

      <div className="relative z-10 px-6 md:px-12 pb-16 md:pb-24 pt-48 md:pt-56">
        <MaskedLine delay={0.15}>
          <p className="text-violet-400 text-xs md:text-sm tracking-[0.35em] uppercase mb-6 flex items-center gap-3">
            <Crosshair size={18} weight="duotone" /> Licensed FFL/SOT located in Johnson County Kentucky
          </p>
        </MaskedLine>

        <div className="font-display uppercase tracking-tighter leading-[0.9]">
          <MaskedLine delay={0.3}>
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[120px] text-violet-400">Appalachian</span>
          </MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[120px] text-[#C8AD7F]">
              Guns <span className="text-primary">LLC</span>
            </span>
          </MaskedLine>
        </div>

        <MaskedLine delay={0.6}>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-white/70">
            Sales, trades, transfers and straight talk. A home based shop with hours to
            accommodate the working folks. Every transaction personally catered to your
            needs — whether it's an 8pm pickup, 9am Sunday fingerprinting &amp; form 4
            filing or just a normal noon meeting, every single person literally gets
            invited to my home to walk in this shop door and they get treated like family.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            data-testid="hero-book-btn"
            href="#book"
            className="bg-primary text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors flex items-center gap-2"
          >
            Book an Appointment <ArrowRight weight="bold" />
          </a>
          <Link
            data-testid="hero-forum-btn"
            to="/forum"
            className="border border-white/30 text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
          >
            Enter the Marketplace
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="relative z-10 border-t border-white/10 px-6 md:px-12 py-4 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.25em] text-white/40"
      >
        <span data-testid="hero-stat-sales" className="text-violet-400">Sales</span>
        <span data-testid="hero-stat-trades">Trades</span>
        <span data-testid="hero-stat-transfers" className="text-violet-400">FFL/SOT Transfers</span>
        <span data-testid="hero-stat-appraisals">Appraisals</span>
        <span className="text-primary" data-testid="hero-stat-community">Community Forum</span>
      </motion.div>
    </section>
  );
};
